<template>
  <div class="calibration_hid">
    <div class="mgx_top_right_fixed" v-if="calibrationHandler.state.initCalibrationStatus == true">
      <div class="top_right_btn mgx_action_btn" @click="closeCalibration">
        <span class="btn_text">{{ $t('end_key_calibration') }}</span>
      </div>
    </div>
    <div class="mgx_top_right_fixed" v-else-if="isSupportKeyGroupSettings">
      <div class="top_right_btn mgx_action_btn group_top_btn" @click="openKeyGroupSettings">
        <span class="btn_text">{{ $t('magnet_select_switch') }}</span>
      </div>
    </div>

    <div class="calibration_container hid" :class="{ align_center: specialCheckCurrent(speciaBehiver.DeviceOf24GCalibration) }">
      <div class="calibration_header">
        <div class="trave mgx_action_btn ghost" @click="startOrEndCalibration">
          {{ isCalibration ? $t('calibration_complete') : $t('start_calibration') }}
        </div>
        <div class="calibration_top_msg" :class="{ royuan: specialCheckCurrent(speciaBehiver.DeviceOfRoyuan) == true }">
          {{ computedDesc }}
          <div class="calibration_top_msg1" v-if="specialCheckCurrent(speciaBehiver.DeviceOf24GCalibration)">
            {{ $t('calibration_top_msg1') }}
          </div>
        </div>
        <div class="calibration_top_msg2" v-if="isRemoveReportID">{{ $t('calibration_notice_desc5') }}</div>
      </div>
      <div class="container">
        <Virtual_devices :is-show-texture="false" :key-class-resolver="resolveCalibrationKeyClass" v-slot="{ kk }">
          <div class="mgx_calibration" :class="{ mgx_calibration_success: calibrationSuccess[kk] }">
            <div class="mgx_travel" :class="{ active: magnetTravel[kk] > 0 }">
              {{ convertTravel(computedTravelValue(kk)) }}
            </div>
            <div class="mgx_flux">
              {{ computedFluxValue(kk) }}
            </div>
          </div>
        </Virtual_devices>
        <mgx_travel v-if="!specialCheckCurrent(speciaBehiver.DeviceOf24GCalibration)" :travel="travel" :monitorJourney="true" />
      </div>

      <div class="calibration_all" :style="{ width: `${currentDevice.width + computedCalibrationMessageWidth}px` }">
        <div class="calibration_content">
          <div class="calibration_des">{{ $t('calibration_notice_desc1_pro') }}</div>
          <div class="calibration_des">{{ $t('calibration_notice_desc2') }}</div>
          <div class="calibration_des">{{ $t('calibration_notice_desc4') }}</div>
        </div>
        <skip />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentDevice } from '@/profiles/selector'
import { computed, onMounted, ref, reactive } from 'vue'
import { MagnetDynamicReport, magnetSet } from '@/service/driver/magnet_set'
import { DriverService } from '@/service/driver/driver'
import { DriverSocketMsgType } from '@libs/types/message'
import { KK } from '@/define/define'
import { convertTravel } from '@/tool/utility'
import { calibrationHandler } from '@/service/driver/calibration_set'
import { magnetSetKeyGroup } from '@/service/driver/magnet_key_group'
import mgx_travel from '@/components/mgx_set/mgx_travel.vue'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import Skip from './skip.vue'
import Virtual_devices from '../device/virtual_devices.vue'
import i18n from '@/languages/i18n'
import { convertTravelForCalibration } from '@/service/driver/travel_converter'

let { t } = i18n.global

const computedDesc = computed(() => {
  if (specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2)) {
    return t('calibration_top_msg_V2')
  }
  if (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan) == false) {
    return t('calibration_top_msg')
  }
  return specialCheckCurrent(speciaBehiver.DeviceOf24GCalibration) ? t('calibration_royuan_24g_top_msg') : t('calibration_royuan_top_msg')
})
const isRemoveReportID = ref(false)
const initRemoveReportID = () => {
  if (localStorage.getItem('isRemoveReportID') == 'true') {
    isRemoveReportID.value = true
  }
}

const computedCalibrationMessageWidth = computed(() => {
  if (specialCheckCurrent(speciaBehiver.DeviceOf24GCalibration)) {
    return 60
  }
  return 320
})

const isSupportKeyGroupSettings = ref(false)

const initKeyGroupSettingsSupport = async () => {
  try {
    if (specialCheckCurrent(speciaBehiver.DeviceOfRoyuan)) {
      isSupportKeyGroupSettings.value = true
      return
    }

    if (specialCheckCurrent(speciaBehiver.SupportAxisKeysGroup)) {
      isSupportKeyGroupSettings.value = await magnetSet.fwIsSupportSingleKeyDeadZone()
    }
  } catch {
    isSupportKeyGroupSettings.value = false
  }
}

const isCalibration = ref(false)
// 存储校准成功的数据
const calibrationSuccess = reactive<Record<KK, boolean>>({})
const magnetTravel = reactive<Record<KK, number>>({})
const magnetFlux = reactive<Record<KK, number>>({})

const clearMagnetValues = (target: Record<KK, number>) => {
  for (const key in target) {
    delete target[key]
  }
}

const resolveCalibrationKeyClass = (): string[] => {
  return ['device_key_mgx_calibration']
}

const travel = computed(() => {
  if (Object.keys(magnetTravel).length === 0) {
    return 0
  }
  return Math.max(...Object.values(magnetTravel))
})

const isAbove6800 = specialCheckCurrent(speciaBehiver.DeviceTravelAbove6800)
const above6800Scale = 6800 / 3500

const magnetHandlers = () => {
  let isUpdateScheduled = false
  const travelBuffer = new Map<KK, number>()
  const fluxBuffer = new Map<KK, number>()

  const applyUpdates = () => {
    travelBuffer.forEach((value, key) => {
      if (magnetTravel[key] !== value) {
        magnetTravel[key] = value
      }
    })
    fluxBuffer.forEach((value, key) => {
      if (magnetFlux[key] !== value) {
        magnetFlux[key] = value
      }
    })
    travelBuffer.clear()
    fluxBuffer.clear()
    isUpdateScheduled = false
  }

  DriverService.instance.setHandler(DriverSocketMsgType.MSMagnetDynamicKey, (res: MagnetDynamicReport[]) => {
    res = convertTravelForCalibration(res)
    // 如果res是空数组，并且之前有数据的话，需要清空之前的数据
    if (res.length == 0 && Object.keys(magnetTravel).length > 0) {
      clearMagnetValues(magnetTravel)
      travelBuffer.clear()
      return
    }
    res.forEach((item) => {
      travelBuffer.set(item.Key, item.Value)
    })
    if (!isUpdateScheduled) {
      isUpdateScheduled = true
      requestAnimationFrame(applyUpdates)
    }
  })
  DriverService.instance.setHandler(DriverSocketMsgType.MSMagnetFluxKey, (res: MagnetDynamicReport[]) => {
    if (res.length == 0 && Object.keys(magnetFlux).length > 0) {
      clearMagnetValues(magnetFlux)
      fluxBuffer.clear()
      return
    }
    res.forEach((item) => {
      fluxBuffer.set(item.Key, item.Value)
    })
    if (!isUpdateScheduled) {
      isUpdateScheduled = true
      requestAnimationFrame(applyUpdates)
    }
  })
}

onMounted(() => {
  calibrationHandler.startAllKeysWatchTrave()
  magnetHandlers()
  handlerCalibration()
  initRemoveReportID()
  initKeyGroupSettingsSupport()
})

const handlerCalibration = () => {
  DriverService.instance.setHandler(DriverSocketMsgType.MSMagnetWatchCalibratin, (res: { [index: KK]: number }) => {
    for (const key in res) {
      if (res[key] > 0 && !calibrationSuccess[key]) {
        calibrationSuccess[key] = true
      }
    }
  })
}

const currentDevice = computed(() => getCurrentDevice.value)
// 获取magnet值
const computedTravelValue = (kk: KK): number => {
  const value = magnetTravel[kk] ?? 0
  if (isAbove6800) {
    return Math.min(value / above6800Scale, 3500)
  }
  return value
}
const computedFluxValue = (kk: KK): number => {
  return magnetFlux[kk] ?? 0
}

// 开启或者关闭行程校准
const startOrEndCalibration = async () => {
  isCalibration.value = !isCalibration.value
  if (isCalibration.value) {
    // 开始校准
    await calibrationHandler.calibrationAllKeys(true)
  } else {
    // 结束校准
    await endTrave()
  }
}
const clearCalibrationData = () => {
  clearMagnetValues(magnetTravel)
  clearMagnetValues(magnetFlux)
}

const openKeyGroupSettings = async () => {
  await magnetSetKeyGroup.openSettingsFromCalibration()
}

const endTrave = async () => {
  console.log('结束行程校准')
  clearCalibrationData()
  await calibrationHandler.calibrationAllKeys(false)
  await calibrationHandler.endAllKeysWatchTrave()
  calibrationHandler.state.isShowHidCalibration = false
  if (calibrationHandler.state.initCalibrationStatus == false) {
    calibrationHandler.markCalibrationSuccess()
  }
  // 校准结束，补充执行 initializeDevice 期间因校准页显示而跳过的全部设备初始化
  await DriverService.instance.completePostCalibrationInit()
}
// 结束校准
const closeCalibration = async () => {
  console.log('关闭行程校准')
  if (isCalibration.value) {
    await endTrave()
  } else {
    clearCalibrationData()
    await calibrationHandler.endAllKeysWatchTrave()
    calibrationHandler.state.isShowHidCalibration = false
    // 校准结束，补充执行 initializeDevice 期间因校准页显示而跳过的全部设备初始化
    await DriverService.instance.completePostCalibrationInit()
  }
}
</script>

<style scoped lang="less">
.calibration_hid {
  width: 100%;
  z-index: 1;
  display: flex;
  justify-content: center;
  height: 100%;
}

.hid {
  margin-top: 68px;
  display: flex;
  flex-direction: column;

  .align_center {
    align-items: center;
  }
}

.right {
  margin-top: 252px;
}

.calibration_header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.trave {
  padding: 0 48px;
  height: 52px;
  line-height: 52px;
  margin-bottom: 22px;
  font-size: 20px;
  font-weight: 800;

  &:hover {
    transform: translateY(-4px);
  }

  &:active {
    transform: translateY(-1px);
  }
}

.group_top_btn {
  min-width: 168px;

  &:hover {
    transform: translateY(-2px);
  }
}

.container {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 50px;
  margin: 30px 0;

  :deep(.board_view) {
    transform: scale(1.2);
    transform-origin: right center;
    background: var(--glass-ghost-bg);
    backdrop-filter: var(--glass-ghost-blur);
    border: 1px solid var(--glass-ghost-border);
    border-radius: var(--radius-l);
    padding: 20px;
  }

  :deep(.mgx_pressed) {
    margin-left: 0; /* 覆盖原本的左边距，交由 gap 接管 */
    height: auto !important; /* 让它与键盘等高 */
    transform: scale(1.2);
    transform-origin: left center;
  }
}

.mgx_calibration {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 auto;
  font-weight: normal;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  background: var(--color-white);
  backdrop-filter: blur(5px);
  transition: var(--transition-base);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: var(--color-brand-1);
    z-index: 5;

    .mgx_travel,
    .mgx_flux {
      opacity: 1;
    }
  }
}

.mgx_calibration_success {
  background: var(--brand-color) !important;
  border-color: var(--color-brand-1) !important;
  box-shadow: 0 0 10px rgba(var(--color-brand-2-rgb), 0.3);

  .mgx_travel,
  .mgx_flux {
    color: var(--color-white) !important;
    opacity: 1 !important;
  }
}

.mgx_travel {
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  color: var(--color-dark);
  font-weight: bold;
  font-size: 11px;
  text-align: center;
  padding: 0 auto;
  transition: var(--transition-base);

  // 按下
  &.active {
    color: var(--color-brand-1);
    font-weight: 900;
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  }
}

.mgx_flux {
  position: absolute;
  top: 1px;
  left: 0;
  right: 0;
  color: var(--color-dark);
  font-weight: 500;
  font-size: 10px;
  text-align: center;
  padding: 0px auto;
  transition: var(--transition-base);
}

.calibration_top_msg {
  font-size: 20px;
  color: var(--color-white);
  margin-bottom: 38px;

  &.royuan {
    font-weight: bold;
  }

  .calibration_top_msg1 {
    width: 100%;
    text-align: center;
    font-size: 18px;
    margin-top: 16px;
    color: rgba(242, 242, 242, 0.8);
    font-weight: normal;
  }
}

.calibration_top_msg2 {
  font-size: 16px;
  color: var(--color-white);
  line-height: 1.5;
  font-weight: bold;
  margin-bottom: 30px;
}

.calibration_all {
  font-size: 36px;
  color: var(--color-white);
  text-align: center;
  order: 2;
  width: 100%;
  // background-color: yellow;
}

.calibration_content {
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: center;

  .calibration_title {
    font-size: 20px;
    color: var(--color-white);
    text-align: start;
    margin-bottom: 14px;
    font-weight: bold;
  }

  .calibration_des {
    font-size: 18px;
    line-height: 40px;
    text-align: start;
  }
}
</style>
